import { generateScheme } from "./generate.js";
import { hexToHslTriplet, ROLE_NAMES, ROLE_TO_CSSVAR, type RoleColors } from "./roles.js";
import type { ThemeConfig, ThemeMode } from "./types.js";

/** The generated chrome roles for a mode, with the user's per-role overrides
 *  applied on top (the "drill into a generated value" axis). */
export function resolveThemeColors(theme: ThemeConfig, mode: ThemeMode): RoleColors {
  const seed = mode === "dark" ? (theme.sourceDark ?? theme.source) : theme.source;
  const generated = generateScheme(seed, theme.variant, mode === "dark");
  const overrides = theme.roleOverrides[mode];
  const out = {} as RoleColors;
  for (const role of ROLE_NAMES) out[role] = overrides[role] ?? generated[role];
  return out;
}

/** Map resolved role colors onto the app's CSS variables as `H S% L%` triplets
 *  (Tailwind reads them via `hsl(var(--x))`). Returns `{ "--background": ... }`. */
export function resolveCssVars(roles: RoleColors): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const role of ROLE_NAMES) {
    const targets = ROLE_TO_CSSVAR[role];
    if (!targets) continue;
    const triplet = hexToHslTriplet(roles[role]);
    for (const cssVar of targets) vars[cssVar] = triplet;
  }
  return vars;
}

/** Convenience: resolved CSS vars for a theme + mode in one call. */
export function themeCssVars(theme: ThemeConfig, mode: ThemeMode): Record<string, string> {
  return resolveCssVars(resolveThemeColors(theme, mode));
}
